import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { OPEN_CONVERSATION_KEY } from './ChatwootAssignmentToastListener';

export {
  buildUnreadFabPreview,
  buildUnreadFabPreviewList,
  enrichFabPreviewListWithProfiles,
  enrichFabPreviewWithProfile
} from '../utils/chatwootContactDisplay';

function WhatsAppIcon({ size = 28 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
      />
    </svg>
  );
}

function buildItemsSignature(items = []) {
  return items.map((item) => `${item.conversationId}:${item.lastMessageId}:${item.convUnread}`).join('|');
}

export default function ChatwootIncomingMessageFab({
  enabled = false,
  unreadCount = 0,
  items = []
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideDir, setSlideDir] = useState(0);
  const [tabVisible, setTabVisible] = useState(() =>
    typeof document !== 'undefined' ? !document.hidden : true
  );

  const prevUnreadRef = useRef(unreadCount);
  const prevItemsSignatureRef = useRef('');
  const itemsSignature = useMemo(() => buildItemsSignature(items), [items]);

  const onConversaciones = location.pathname.startsWith('/conversaciones');
  const safeIndex = items.length ? Math.min(activeIndex, items.length - 1) : 0;
  const preview = items[safeIndex] || null;
  const hasMultiple = items.length > 1;

  const shouldShow =
    enabled &&
    unreadCount > 0 &&
    items.length > 0 &&
    !(onConversaciones && tabVisible);

  useEffect(() => {
    setActiveIndex(0);
    setSlideDir(0);
  }, [itemsSignature]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibility = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    if (unreadCount <= 0) {
      setExpanded(false);
      setActiveIndex(0);
      prevUnreadRef.current = 0;
      prevItemsSignatureRef.current = '';
      return;
    }
    prevUnreadRef.current = unreadCount;
    prevItemsSignatureRef.current = itemsSignature;
  }, [unreadCount, itemsSignature]);

  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  useEffect(() => {
    if (onConversaciones && tabVisible) {
      setExpanded(false);
    }
  }, [onConversaciones, tabVisible]);

  const collapseCard = useCallback(() => {
    setExpanded(false);
  }, []);

  useEffect(() => {
    if (!expanded || typeof document === 'undefined') return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') collapseCard();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [expanded, collapseCard]);

  const goPrev = useCallback(() => {
    if (!hasMultiple) return;
    setSlideDir(-1);
    setActiveIndex((idx) => (idx - 1 + items.length) % items.length);
  }, [hasMultiple, items.length]);

  const goNext = useCallback(() => {
    if (!hasMultiple) return;
    setSlideDir(1);
    setActiveIndex((idx) => (idx + 1) % items.length);
  }, [hasMultiple, items.length]);

  const openConversation = useCallback(() => {
    collapseCard();
    if (!preview?.conversationId) {
      navigate('/conversaciones');
      return;
    }
    try {
      sessionStorage.setItem(OPEN_CONVERSATION_KEY, String(preview.conversationId));
    } catch {
      // ignore
    }
    navigate('/conversaciones');
  }, [collapseCard, navigate, preview?.conversationId]);

  if (!shouldShow || typeof document === 'undefined') return null;

  const badge = Math.min(99, Number(unreadCount || 1));
  const badgeLabel = badge > 99 ? '99+' : String(badge);
  const badgeClass =
    badgeLabel.length >= 3
      ? 'crm-cw-wa-fab__badge crm-cw-wa-fab__badge--max'
      : badgeLabel.length === 2
        ? 'crm-cw-wa-fab__badge crm-cw-wa-fab__badge--dbl'
        : 'crm-cw-wa-fab__badge';
  const title = preview?.title || 'WhatsApp';
  const subtitle = preview?.subtitle || '';
  const body = preview?.body || `Tenés ${unreadCount} mensaje${unreadCount === 1 ? '' : 's'} sin leer`;
  const initials = preview?.initials || 'WA';
  const convUnread = Number(preview?.convUnread || 0);

  return createPortal(
    <div className={`crm-cw-wa-fab${expanded ? ' is-expanded' : ''}`} aria-live="polite">
      {expanded ? (
        <>
          <button
            type="button"
            className="crm-cw-wa-fab__backdrop"
            onClick={collapseCard}
            aria-label="Cerrar aviso de mensaje"
          />
          <div className="crm-cw-wa-fab__card" role="dialog" aria-modal="true" aria-label="Mensaje sin leer">
            <div className="crm-cw-wa-fab__card-accent" aria-hidden="true" />

            <header className="crm-cw-wa-fab__card-head">
              <div className="crm-cw-wa-fab__card-head-main">
                <p className="crm-cw-wa-fab__card-eyebrow">
                  <span className="crm-cw-wa-fab__card-eyebrow-icon" aria-hidden="true">
                    <WhatsAppIcon size={12} />
                  </span>
                  Mensaje sin leer
                </p>
                <div className="crm-cw-wa-fab__status-row">
                  <span className="crm-cw-wa-fab__status-pill crm-cw-wa-fab__status-pill--live">
                    <span className="crm-cw-wa-fab__status-dot" aria-hidden="true" />
                    Nuevo mensaje
                  </span>
                  {unreadCount > 1 ? (
                    <span className="crm-cw-wa-fab__status-pill crm-cw-wa-fab__status-pill--count">
                      {unreadCount} sin leer
                    </span>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className="crm-cw-wa-fab__close"
                onClick={collapseCard}
                aria-label="Minimizar aviso"
              >
                ×
              </button>
            </header>

            <div className="crm-cw-wa-fab__card-main">
              <div
                key={`${preview?.conversationId}-${preview?.lastMessageId}`}
                className={`crm-cw-wa-fab__slide${slideDir < 0 ? ' is-back' : slideDir > 0 ? ' is-forward' : ''}`}
              >
                <article className="crm-cw-wa-fab__contact-panel">
                  <div className="crm-cw-wa-fab__identity">
                    <div className="crm-cw-wa-fab__avatar" aria-hidden="true">
                      {initials}
                    </div>
                    <div className="crm-cw-wa-fab__identity-copy">
                      <strong className="crm-cw-wa-fab__card-title">{title}</strong>
                      {subtitle ? <span className="crm-cw-wa-fab__card-sub">{subtitle}</span> : null}
                    </div>
                    {convUnread > 1 ? (
                      <span className="crm-cw-wa-fab__conv-badge" title={`${convUnread} en esta conversación`}>
                        {convUnread}
                      </span>
                    ) : null}
                  </div>

                  <div className="crm-cw-wa-fab__message-preview">
                    <span className="crm-cw-wa-fab__message-label">Último mensaje</span>
                    <p className="crm-cw-wa-fab__card-body">{body}</p>
                  </div>
                </article>
              </div>
            </div>

            {hasMultiple ? (
              <div className="crm-cw-wa-fab__pager" role="navigation" aria-label="Recorrer conversaciones sin leer">
                <button
                  type="button"
                  className="crm-cw-wa-fab__pager-btn"
                  onClick={goPrev}
                  aria-label="Conversación anterior"
                >
                  <span aria-hidden="true">‹</span>
                  <span>Anterior</span>
                </button>

                <div className="crm-cw-wa-fab__pager-meta">
                  <span className="crm-cw-wa-fab__pager-count">
                    {safeIndex + 1} / {items.length}
                  </span>
                  <span className="crm-cw-wa-fab__pager-dots" aria-hidden="true">
                    {items.map((item, idx) => (
                      <span
                        key={item.conversationId}
                        className={`crm-cw-wa-fab__pager-dot${idx === safeIndex ? ' is-active' : ''}`}
                      />
                    ))}
                  </span>
                </div>

                <button
                  type="button"
                  className="crm-cw-wa-fab__pager-btn"
                  onClick={goNext}
                  aria-label="Siguiente conversación"
                >
                  <span>Siguiente</span>
                  <span aria-hidden="true">›</span>
                </button>
              </div>
            ) : null}

            <footer className="crm-cw-wa-fab__card-foot">
              <button type="button" className="crm-cw-wa-fab__card-action" onClick={openConversation}>
                <span className="crm-cw-wa-fab__card-action-icon" aria-hidden="true">
                  <WhatsAppIcon size={16} />
                </span>
                <span className="crm-cw-wa-fab__card-action-label">Abrir conversación</span>
                <span className="crm-cw-wa-fab__action-arrow" aria-hidden="true">
                  →
                </span>
              </button>
            </footer>
          </div>
        </>
      ) : (
        <div className="crm-cw-wa-fab__rail">
          <button
            type="button"
            className="crm-cw-wa-fab__btn"
            onClick={() => setExpanded(true)}
            aria-label={`${unreadCount} mensaje${unreadCount === 1 ? '' : 's'} sin leer. Ver conversaciones.`}
            title="Ver mensajes sin leer"
          >
            <span className="crm-cw-wa-fab__pulse" aria-hidden="true" />
            <span className="crm-cw-wa-fab__icon">
              <WhatsAppIcon size={22} />
            </span>
            {badge > 0 ? <span className={badgeClass}>{badgeLabel}</span> : null}
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}
